import {
  Alert,
  Paragraph,
  Stack,
  Wizard,
  useWizardPage,
  Title
} from '@efast_public/fjd-component-library';
import { useTranslation } from 'react-i18next';
import React from 'react';
import { WeiterleitungsWizardState } from '../../../../modals/ReceiverSettingsWizard/Wizard.types';
import {
  PublicServiceSyncSnapshot,
  SubmissionSchemaSyncSnapshot,
  SyncResultDto
} from '../../../../api/hooks/useSyncResult';

function Summary() {
  const { t } = useTranslation();

  const { wizardState } = useWizardPage({
    headline: (state) =>
      `Schritt ${state.currentPageIndex + 1} von ${state.pagesLength}`,
    primaryButtonLabel: t('configAssistent.summary.primaryButton')
  });

  const state = wizardState.state as WeiterleitungsWizardState | undefined;
  const syncResult: SyncResultDto | undefined = state?.syncResult;

  const destinationNameById = new Map<string, string>(
    (state?.orgUnits?.organizationalUnitDtos ?? []).flatMap((orgUnit) =>
      orgUnit.destinationDtos.map((destination) => [
        destination.destinationId,
        orgUnit.name
      ])
    )
  );

  const groupByDestination = <T extends { destinationId: string }>(
    items: T[] | undefined
  ) => {
    const grouped = new Map<string, T[]>();
    (items ?? []).forEach((item) => {
      const entries = grouped.get(item.destinationId) ?? [];
      entries.push(item);
      grouped.set(item.destinationId, entries);
    });
    return grouped;
  };

  const removedPublicServicesByDestination = groupByDestination(
    syncResult?.removedPublicServices
  );
  const reactivatedPublicServicesByDestination =
    groupByDestination<PublicServiceSyncSnapshot>(
      syncResult?.reactivatedPublicServices
    );
  const addedSubmissionSchemasByDestination =
    groupByDestination<SubmissionSchemaSyncSnapshot>(
      syncResult?.addedSubmissionSchemas
    );
  const removedSubmissionSchemasByDestination =
    groupByDestination<SubmissionSchemaSyncSnapshot>(
      syncResult?.removedSubmissionSchemas
    );
  const reactivatedSubmissionSchemasByDestination =
    groupByDestination<SubmissionSchemaSyncSnapshot>(
      syncResult?.reactivatedSubmissionSchemas
    );

  return (
    <Wizard.Page>
      <Stack direction="column" spacing="xs">
        <Title level={2} visualLevel={6}>
          {t('configAssistent.summary.headline')}
        </Title>
        <Paragraph>{t('configAssistent.summary.descriptionText')}</Paragraph>
        <Alert
          message={t('receiverSettingsWizard.summary.fitCoSuccess')}
          variant="success"
        />
        {syncResult && (
          <Stack direction="column" spacing="xs">
            {syncResult.importedOrgUnitNames.length > 0 && (
              <Alert
                variant="success"
                message={t('receiverSettingsWizard.summary.importedOrgUnits', {
                  names: syncResult.importedOrgUnitNames.join(', '),
                  count: syncResult.importedOrgUnitNames.length
                })}
              />
            )}
            {syncResult.deactivatedOrgUnitNames.length > 0 && (
              <Alert
                variant="warning"
                message={t(
                  'receiverSettingsWizard.summary.deactivatedOrgUnits',
                  {
                    names: syncResult.deactivatedOrgUnitNames.join(', '),
                    count: syncResult.deactivatedOrgUnitNames.length
                  }
                )}
              />
            )}
            {syncResult.deletedOrgUnitNames.length > 0 && (
              <Alert
                variant="warning"
                message={t('receiverSettingsWizard.summary.deletedOrgUnits', {
                  names: syncResult.deletedOrgUnitNames.join(', '),
                  count: syncResult.deletedOrgUnitNames.length
                })}
              />
            )}
            {syncResult.addedPublicServices.length > 0 && (
              <Alert
                variant="success"
                message={t(
                  'receiverSettingsWizard.summary.addedPublicServices',
                  { count: syncResult.addedPublicServices.length }
                )}
              />
            )}
            {Array.from(removedPublicServicesByDestination.values()).map(
              (services) => (
                <Alert
                  key={services[0].destinationId}
                  variant="warning"
                  message={t(
                    'receiverSettingsWizard.summary.removedPublicServices',
                    {
                      destination:
                        destinationNameById.get(services[0].destinationId) ??
                        services[0].destinationId,
                      services: services
                        .map((service) => service.identifier)
                        .join(', '),
                      count: services.length
                    }
                  )}
                />
              )
            )}
            {Array.from(reactivatedPublicServicesByDestination.values()).map(
              (services) => (
                <Alert
                  key={services[0].destinationId}
                  variant="success"
                  message={t(
                    'receiverSettingsWizard.summary.reactivatedPublicServices',
                    {
                      destination:
                        destinationNameById.get(services[0].destinationId) ??
                        services[0].destinationId,
                      services: services
                        .map((service) => service.identifier)
                        .join(', '),
                      count: services.length
                    }
                  )}
                />
              )
            )}
            {Array.from(addedSubmissionSchemasByDestination.values()).map(
              (schemas) => (
                <Alert
                  key={schemas[0].destinationId}
                  variant="success"
                  message={t(
                    'receiverSettingsWizard.summary.addedSubmissionSchemas',
                    {
                      destination:
                        destinationNameById.get(schemas[0].destinationId) ??
                        schemas[0].destinationId,
                      schemas: schemas
                        .map(
                          (schema) =>
                            `${schema.identifier} (${schema.schemaUri})`
                        )
                        .join(', '),
                      count: schemas.length
                    }
                  )}
                />
              )
            )}
            {Array.from(removedSubmissionSchemasByDestination.values()).map(
              (schemas) => (
                <Alert
                  key={schemas[0].destinationId}
                  variant="warning"
                  message={t(
                    'receiverSettingsWizard.summary.removedSubmissionSchemas',
                    {
                      destination:
                        destinationNameById.get(schemas[0].destinationId) ??
                        schemas[0].destinationId,
                      schemas: schemas
                        .map(
                          (schema) =>
                            `${schema.identifier} (${schema.schemaUri})`
                        )
                        .join(', '),
                      count: schemas.length
                    }
                  )}
                />
              )
            )}
            {Array.from(reactivatedSubmissionSchemasByDestination.values()).map(
              (schemas) => (
                <Alert
                  key={schemas[0].destinationId}
                  variant="success"
                  message={t(
                    'receiverSettingsWizard.summary.reactivatedSubmissionSchemas',
                    {
                      destination:
                        destinationNameById.get(schemas[0].destinationId) ??
                        schemas[0].destinationId,
                      schemas: schemas
                        .map(
                          (schema) =>
                            `${schema.identifier} (${schema.schemaUri})`
                        )
                        .join(', '),
                      count: schemas.length
                    }
                  )}
                />
              )
            )}
            {syncResult.newlyImported.length > 0 && (
              <Alert
                variant="success"
                message={t('receiverSettingsWizard.summary.addedSchemaUris', {
                  total: syncResult.newlyImported.length
                })}
              />
            )}
            {syncResult.syncStatus.some((s) => !s.dataAvailable) && (
              <Alert
                variant="warning"
                message={t('receiverSettingsWizard.summary.updatedSchemaUris', {
                  pending: syncResult.syncStatus.filter((s) => !s.dataAvailable)
                    .length,
                  total: syncResult.syncStatus.length
                })}
              />
            )}
            {syncResult.importedOrgUnitNames.length === 0 &&
              syncResult.deactivatedOrgUnitNames.length === 0 &&
              syncResult.deletedOrgUnitNames.length === 0 &&
              syncResult.addedPublicServices.length === 0 &&
              syncResult.removedPublicServices.length === 0 &&
              syncResult.reactivatedPublicServices.length === 0 &&
              syncResult.addedSubmissionSchemas.length === 0 &&
              syncResult.removedSubmissionSchemas.length === 0 &&
              syncResult.reactivatedSubmissionSchemas.length === 0 &&
              syncResult.newlyImported.length === 0 &&
              !syncResult.syncStatus.some((s) => !s.dataAvailable) && (
                <Alert
                  variant="info"
                  message={t('receiverSettingsWizard.summary.noChanges')}
                />
              )}
          </Stack>
        )}
      </Stack>
    </Wizard.Page>
  );
}

export default Summary;
